/**
 * NewForm design-system Button.
 *
 * New components for the v2 design pass live alongside the legacy shadcn
 * components (`components/ui/button.tsx`) so existing callers keep working
 * unchanged. New code should import from this path:
 *
 *   import { Button } from "@/components/ui/design-system/button";
 *
 * The legacy Button stays at `@/components/ui/button` until everything
 * has migrated. When that day comes, this file becomes the only source of
 * truth and the legacy file gets deleted.
 */

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";
import { CONTROL_PRESS } from "./surface-styles";

const buttonVariants = cva(
  // Base spec mirrors `nf-design-system/preview/components-buttons.html`
  // exactly: `font: 500 14px/1` + 6px radius + 8px icon-text gap. Using
  // arbitrary values `text-[14px]`, `font-[500]`, `leading-none` instead
  // of `text-sm`/`font-medium` because the project overrides Tailwind's
  // `--font-weight-bold`/`-semibold` in `globals.css` — going arbitrary
  // makes the button immune to any further weight-scale tweaks.
  // `CONTROL_PRESS` carries the one press every DS control shares (GUIDELINES,
  // "One curve for everything"): 150ms on the house curve, `scale(0.97)` on
  // `:active`, dropped under reduced motion. Its transition covers
  // background colour and transform, which is what the variants change.
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-nf-ds-button font-nf-ds-sans text-[14px] font-[500] leading-none ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    CONTROL_PRESS,
  ),
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        brand:
          "bg-nf-ds-purple-product text-nf-ds-white hover:bg-nf-ds-purple-700",
        // The single action treatment for overview-class surfaces. Purple
        // is reserved for links, focus rings and brand marks — a page of
        // filled purple buttons has no hierarchy left to spend.
        ink: "bg-[image:var(--nf-ds-ink-action-grad)] text-nf-ds-ink-action-fg hover:opacity-90 shadow-nf-ds-xs",
        // Soft-rounded CTAs used across the auth shell (Continue with Google,
        // Sign in →, etc). 2xl radius matches the inputs sitting next to them.
        // Variant names kept as "pill-*" for back-compat with existing callers.
        "pill-primary":
          "h-12 px-nf-ds-6 rounded-nf-ds-2xl bg-nf-ds-black text-nf-ds-white hover:bg-nf-ds-gray-800 shadow-nf-ds-xs",
        "pill-outline":
          "h-12 px-nf-ds-6 rounded-nf-ds-2xl bg-background border border-border text-foreground hover:bg-nf-ds-bg-surface shadow-nf-ds-xs",
        // `--destructive` carries the DS error accent — the same
        // hsl(0 84.2% 60.2%) the `err` alert uses for its border and dot.
        // The Tailwind `red-600` this used before was a shade off it and
        // tracked nothing.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-nf-ds-xs",
        outline:
          "border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-nf-ds-control px-4",
        sm: "h-nf-ds-control-sm px-3 text-[13px] gap-1.5",
        lg: "h-11 px-8",
        icon: "h-nf-ds-control w-nf-ds-control",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
